import { create } from 'zustand';
import {
  creativeSkillsApi,
  type SkillDefinition,
  type SkillFramework,
  type ScriptTemplate,
  type GenerationPlatform,
  type SkillPipeline,
} from '@/services/api';

type View =
  | { kind: 'overview' }
  | { kind: 'skill'; skillId: string }
  | { kind: 'template'; templateId: string };

interface SkillsState {
  view: View;
  loading: boolean;
  error: string | null;

  definitions: SkillDefinition[];
  frameworks: SkillFramework[];
  templates: ScriptTemplate[];
  platforms: GenerationPlatform[];
  pipelines: SkillPipeline[];

  selectedSkill: SkillDefinition | null;

  setView: (view: View) => void;
  fetchAll: () => Promise<void>;
  fetchSkillDetail: (skillId: string) => Promise<void>;
}

export const useSkillsStore = create<SkillsState>((set) => ({
  view: { kind: 'overview' },
  loading: false,
  error: null,
  definitions: [],
  frameworks: [],
  templates: [],
  platforms: [],
  pipelines: [],
  selectedSkill: null,

  setView: (view) => set({ view, error: null }),

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const results = await Promise.allSettled([
        creativeSkillsApi.listDefinitions(),
        creativeSkillsApi.listFrameworks(),
        creativeSkillsApi.listTemplates(),
        creativeSkillsApi.listVideoPlatforms(),
        creativeSkillsApi.listPipelines(),
      ]);

      const [defs, fws, tpls, plats, pipes] = results;

      // Normalize backend field names to match UI interfaces
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const normalizeFramework = (fw: any): SkillFramework => ({
        id: fw.framework_id ?? fw.id,
        name: fw.name,
        description: fw.description,
        archetype: fw.framework_type ?? fw.archetype,
        funnel_stages: fw.funnel_stages,
        platforms: fw.platforms,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const normalizeTemplate = (t: any): ScriptTemplate => ({
        id: t.template_id ?? t.id,
        name: t.name,
        script_type: t.script_type,
        description: t.description,
        segment_count: t.segment_count,
        total_duration_seconds: Array.isArray(t.total_duration_range)
          ? t.total_duration_range[0]
          : t.total_duration_seconds,
        visual_style: t.visual_style,
        audio_style: t.audio_style,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const normalizePlatform = (p: any): GenerationPlatform => ({
        id: p.platform_id ?? p.id,
        name: p.name,
        type: p.type ?? p.generation_speed ?? 'ai_video',
        capabilities: p.capabilities ?? p.best_for,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const normalizePipeline = (pipe: any): SkillPipeline => {
        const steps: string[] = ['brief', 'angles', 'scripts'];
        if (pipe.generate_storyboard) steps.push('storyboard');
        if (pipe.video_prompts_per_script) steps.push('video_prompts');
        return {
          id: pipe.config_id ?? pipe.id,
          name: pipe.name,
          description: pipe.description,
          steps: pipe.steps ?? steps,
        };
      };

      set({
        definitions: defs.status === 'fulfilled' ? defs.value.skills : [],
        frameworks: fws.status === 'fulfilled'
          ? fws.value.frameworks.map(normalizeFramework)
          : [],
        templates: tpls.status === 'fulfilled'
          ? tpls.value.templates.map(normalizeTemplate)
          : [],
        platforms: plats.status === 'fulfilled'
          ? plats.value.platforms.map(normalizePlatform)
          : [],
        pipelines: pipes.status === 'fulfilled'
          ? pipes.value.pipelines.map(normalizePipeline)
          : [],
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load skills data' });
    } finally {
      set({ loading: false });
    }
  },

  fetchSkillDetail: async (skillId) => {
    set({ loading: true, error: null, selectedSkill: null });
    try {
      const skill = await creativeSkillsApi.getDefinition(skillId);
      set({ selectedSkill: skill });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load skill detail' });
    } finally {
      set({ loading: false });
    }
  },
}));
